import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useTenantStore } from "../../stores/tenant";
import { useToastStore } from "../../stores/toast";

interface Forward {
  id: string;
  tenantId: string;
  conditionIntent: string;
  targetKind: "phone" | "user";
  targetValue: string;
  priority: number;
  enabled: boolean;
}

export function ForwardsList() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const push = useToastStore((s) => s.push);
  const [rules, setRules] = useState<Forward[]>([]);
  const [intent, setIntent] = useState("*");
  const [digit, setDigit] = useState("");
  const [targetKind, setTargetKind] = useState<"phone" | "user">("phone");
  const [targetValue, setTargetValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Affiche joliment le déclencheur (une règle « dtmf:9 » -> « Touche 9 »).
  const triggerLabel = (intent: string) =>
    intent.startsWith("dtmf:") ? `Touche ${intent.slice(5)}` : intent;

  const load = () => {
    if (!tenantId) return;
    api<Forward[]>(`/api/tenants/${tenantId}/forwards`).then(setRules).catch(() => setRules([]));
  };

  useEffect(load, [tenantId]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !targetValue.trim() || saving) return;
    setSaving(true);
    try {
      const d = digit.trim();
      await api(`/api/tenants/${tenantId}/forwards`, {
        method: "POST",
        body: JSON.stringify({
          conditionIntent: d ? `dtmf:${d}` : intent.trim() || "*",
          targetKind,
          targetValue: targetValue.trim(),
          priority: 0,
          enabled: true,
        }),
      });
      setTargetValue("");
      setDigit("");
      push("success", "Redirection ajoutée");
      load();
    } catch {
      push("danger", "Échec de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!tenantId) return;
    try {
      await api(`/api/tenants/${tenantId}/forwards/${id}`, { method: "DELETE" });
      setRules((prev) => prev.filter((x) => x.id !== id));
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  if (!tenantId) return <div className="page-header"><h1 className="page-header__title">Sélectionne un établissement</h1></div>;

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Redirections</h1>
          <p className="page-header__subtitle">
            Vers qui transférer quand l'agent est bloqué — mot-clé ou touche (« pesez 9 ») en déclencheur.
          </p>
        </div>
      </header>
      <form className="config-form" onSubmit={add}>
        <input
          className="input"
          type="text"
          placeholder="Déclencheur (* ou mot-clé)"
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          disabled={Boolean(digit.trim())}
        />
        <input
          className="input"
          type="text"
          inputMode="numeric"
          maxLength={1}
          placeholder="ou touche (ex 9)"
          value={digit}
          onChange={(e) => setDigit(e.target.value.replace(/[^0-9*#]/g, ""))}
          style={{ maxWidth: "8rem" }}
        />
        <select className="select" value={targetKind} onChange={(e) => setTargetKind(e.target.value as "phone" | "user")}>
          <option value="phone">Numéro</option>
          <option value="user">Membre d'équipe</option>
        </select>
        <input
          className="input config-form__grow"
          type="text"
          placeholder={targetKind === "phone" ? "Numéro (ex +15145551234)" : "ID du membre"}
          value={targetValue}
          onChange={(e) => setTargetValue(e.target.value)}
        />
        <button type="submit" className="btn btn--primary" disabled={saving || !targetValue.trim()}>
          {saving ? "Ajout…" : "Ajouter"}
        </button>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>Déclencheur</th>
            <th>Type</th>
            <th>Cible</th>
            <th>Actif</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rules.length === 0 ? (
            <tr><td colSpan={5} className="table__empty">Aucune redirection — par défaut l'agent crée un rappel.</td></tr>
          ) : (
            rules.map((r) => (
              <tr key={r.id}>
                <td>{triggerLabel(r.conditionIntent)}</td>
                <td>{r.targetKind === "phone" ? "Numéro" : "Membre"}</td>
                <td>{r.targetValue}</td>
                <td>{r.enabled ? "Oui" : "Non"}</td>
                <td>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void remove(r.id)}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ForwardsList;
